(() => {
  const initialize = () => {
    const root = document.querySelector('[data-steep-trial-root]');
    if (!root || root.dataset.trialReady === 'true') return;
    root.dataset.trialReady = 'true';

    const variantId = Number(root.dataset.variantId || 0);
    const price = root.dataset.trialPrice || '$15.96';
    const sku = root.dataset.trialSku || 'STP-TRIAL-OGC-04';
    const buyButtons = Array.from(root.querySelectorAll('[data-trial-buy]'));
    const statusNodes = Array.from(root.querySelectorAll('[data-trial-status]'));
    const sticky = root.querySelector('[data-trial-sticky]');
    const heroButton = root.querySelector('[data-trial-primary]');
    let purchasePending = false;

    const heroMotionTargets = Array.from(root.querySelectorAll('.steep-trial__hero [data-trial-reveal], .steep-trial__hero [data-trial-motion]'));
    requestAnimationFrame(() => requestAnimationFrame(() => heroMotionTargets.forEach((target) => target.classList.add('is-revealed'))));

    const setStatus = (message = '') => statusNodes.forEach((node) => { node.textContent = message; });
    const setButtonState = (disabled, label) => buyButtons.forEach((button) => {
      if (!button.dataset.originalLabel) button.dataset.originalLabel = button.textContent.trim();
      button.disabled = disabled;
      if (label) button.textContent = label;
      else button.textContent = button.dataset.originalLabel;
    });

    const track = (event, details = {}) => {
      const payload = { event, landing_page: 'try-steep', offer: '4-pack-15.96-shipped', variant_id: variantId, sku, value: 15.96, currency: 'USD', ...details };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      window.dispatchEvent(new CustomEvent(event, { detail: payload }));
    };

    const changeCartLine = async (key, quantity) => {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'We couldn’t update the trial pack.');
      return payload;
    };

    const normalizeTrialInCart = async () => {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'We couldn’t check your cart.');

      const trialLines = cart.items.filter((item) => Number(item.variant_id) === variantId);
      if (!trialLines.length) return false;

      for (const [index, item] of trialLines.entries()) {
        const allowedQuantity = index === 0 ? 1 : 0;
        if (Number(item.quantity) !== allowedQuantity) await changeCartLine(item.key, allowedQuantity);
      }
      return true;
    };

    const buy = async () => {
      if (purchasePending) return;
      if (!variantId) {
        setStatus('This trial is still being prepared. Please check back shortly.');
        return;
      }

      purchasePending = true;
      setStatus('Adding your 4-pack…');
      setButtonState(true, 'ADDING…');

      try {
        const alreadyInCart = await normalizeTrialInCart();
        if (!alreadyInCart) {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { '_Trial offer': '4 cans · $15.96 · free contiguous U.S. shipping', '_Landing page': 'try-steep' } }] })
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.description || payload.message || 'We couldn’t add the trial pack.');
          track('steep_trial_add_to_cart');
        }
        setStatus('Added. Taking you to secure checkout…');
        setButtonState(true, 'GOING TO CHECKOUT…');
        track('steep_trial_initiate_checkout');
        window.location.assign('/checkout');
      } catch (error) {
        purchasePending = false;
        setButtonState(false);
        setStatus(error?.message || 'Something went wrong. Please try again.');
      }
    };

    buyButtons.forEach((button) => button.addEventListener('click', buy));

    if (sticky && heroButton) {
      let stickyFrame = 0;
      const updateSticky = () => {
        stickyFrame = 0;
        const shouldShow = heroButton.getBoundingClientRect().bottom < 0;
        sticky.classList.toggle('is-visible', shouldShow);
        sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      };
      const requestStickyUpdate = () => {
        if (stickyFrame) return;
        stickyFrame = requestAnimationFrame(updateSticky);
      };
      window.addEventListener('scroll', requestStickyUpdate, { passive: true });
      document.addEventListener('scroll', requestStickyUpdate, { passive: true, capture: true });
      window.addEventListener('resize', requestStickyUpdate, { passive: true });
      updateSticky();
      window.setInterval(updateSticky, 500);
    }

    const revealTargets = Array.from(root.querySelectorAll('[data-trial-reveal], [data-trial-motion]'));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      revealTargets.forEach((target) => target.classList.add('is-revealed'));
    } else {
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6%', threshold: 0.08 });
      revealTargets.forEach((target) => revealObserver.observe(target));
    }

    const sectionTargets = Array.from(root.querySelectorAll('[data-trial-section]'));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      sectionTargets.forEach((target) => target.classList.add('is-in-view'));
    } else {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.classList.toggle('is-in-view', entry.isIntersecting));
      }, { rootMargin: '0px 0px -6%', threshold: 0.1 });
      sectionTargets.forEach((target) => sectionObserver.observe(target));
    }

    const suppressKlaviyo = () => {
      const popup = document.querySelector('[aria-label="POPUP Form"]');
      const popupClose = popup?.querySelector('[aria-label="Close dialog"], .klaviyo-close-form');

      if (popup instanceof HTMLElement && popupClose instanceof HTMLElement && getComputedStyle(popup).display !== 'none') {
        popupClose.click();
      }

      document.querySelectorAll('[aria-label="POPUP Form"], [class*="kl-teaser-"]').forEach((node) => {
        if (!(node instanceof HTMLElement) || node.closest('[data-steep-trial-root]')) return;
        node.style.setProperty('display', 'none', 'important');
        node.setAttribute('aria-hidden', 'true');
      });
    };
    suppressKlaviyo();
    const klaviyoObserver = new MutationObserver(suppressKlaviyo);
    klaviyoObserver.observe(document.body, { childList: true, subtree: true });

    track('steep_trial_view');
    console.info('[Steep trial] Ready', { variantId, price, sku });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
